import React, { useCallback, useMemo, useState } from "react";
import { Alert, Spinner, Table } from "reactstrap";
import CreditNoteStatusBadge from "./CreditNoteStatusBadge";
import CreditNoteBalance from "./CreditNoteBalance";
import CreditNoteUsageProjects from "./CreditNoteUsageProjects";
import CreditNoteOriginModal from "./CreditNoteOriginModal";
import {
  CREDIT_NOTES_SORT_DEFAULT,
  fmtOrderRupee,
  sortCreditNotesRows,
} from "../../pages/storeSalesDashboardHelpers";

function CustomerCell({ usageProjects }) {
  const rows = Array.isArray(usageProjects) ? usageProjects : [];
  if (rows.length === 0) {
    return <span className="text-muted">—</span>;
  }
  return (
    <ul className="list-unstyled mb-0">
      {rows.map((row) => (
        <li
          key={`${row.project_id}-cust`}
          className={rows.length > 1 ? "mb-1" : ""}
        >
          {row.customer_name || "—"}
        </li>
      ))}
    </ul>
  );
}

function UsageAmountCell({ usageProjects, field }) {
  const rows = Array.isArray(usageProjects) ? usageProjects : [];
  if (rows.length === 0) {
    return <span className="text-muted">—</span>;
  }
  return (
    <ul className="list-unstyled mb-0 text-end">
      {rows.map((row) => {
        const amount = row[field];
        return (
          <li
            key={`${row.project_id}-${field}`}
            className={rows.length > 1 ? "mb-1" : ""}
          >
            {amount != null && Number.isFinite(Number(amount))
              ? fmtOrderRupee(amount)
              : "—"}
          </li>
        );
      })}
    </ul>
  );
}

function SortableTh({ sortKey, label, sortConfig, onSort, className = "" }) {
  const active = sortConfig.key === sortKey;
  const icon = !active ? "⇅" : sortConfig.direction === "asc" ? "▲" : "▼";
  return (
    <th
      role="button"
      className={className}
      onClick={() => onSort(sortKey)}
      aria-sort={
        active
          ? sortConfig.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      {label} {icon}
    </th>
  );
}

export default function CreditNoteListTable({
  creditNotes,
  loading,
  error,
  showCustomerColumn = false,
  detailLinkPrefix,
}) {
  const [statusFilter, setStatusFilter] = useState("");
  const [searchId, setSearchId] = useState("");
  const [sortConfig, setSortConfig] = useState(CREDIT_NOTES_SORT_DEFAULT);
  const [originModalNote, setOriginModalNote] = useState(null);

  const openOriginModal = useCallback((note) => {
    setOriginModalNote(note);
  }, []);

  const closeOriginModal = useCallback(() => {
    setOriginModalNote(null);
  }, []);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const filtered = useMemo(() => {
    let list = Array.isArray(creditNotes) ? [...creditNotes] : [];
    if (statusFilter) {
      list = list.filter((n) => n.status === statusFilter);
    }
    if (searchId.trim()) {
      const q = searchId.trim().toLowerCase();
      list = list.filter((n) =>
        String(n.identifier || "")
          .toLowerCase()
          .includes(q)
      );
    }
    return sortCreditNotesRows(list, sortConfig);
  }, [creditNotes, statusFilter, searchId, sortConfig]);

  const colCount = showCustomerColumn ? 7 : 6;

  if (loading) {
    return (
      <div className="py-4 text-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="danger" fade={false} className="mb-0">
        {error}
      </Alert>
    );
  }

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="text-muted small">Status:</span>
        <button
          type="button"
          className={`btn btn-sm rounded-pill ${
            statusFilter === "" ? "btn-primary" : "btn-outline-secondary"
          }`}
          onClick={() => setStatusFilter("")}
        >
          All
        </button>
        <button
          type="button"
          className={`btn btn-sm rounded-pill ${
            statusFilter === "open" ? "btn-primary" : "btn-outline-secondary"
          }`}
          onClick={() => setStatusFilter("open")}
        >
          Open
        </button>
        <button
          type="button"
          className={`btn btn-sm rounded-pill ${
            statusFilter === "closed" ? "btn-primary" : "btn-outline-secondary"
          }`}
          onClick={() => setStatusFilter("closed")}
        >
          Closed
        </button>
        <input
          type="search"
          className="form-control form-control-sm ms-auto"
          style={{ maxWidth: "14rem" }}
          placeholder="Search identifier"
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
        />
      </div>
      <Table bordered hover responsive className="mb-0 store-sales-table">
        <thead className="store-sales-table__head">
          <tr>
            <SortableTh
              sortKey="identifier"
              label="Identifier"
              sortConfig={sortConfig}
              onSort={handleSort}
            />
            {showCustomerColumn ? (
              <SortableTh
                sortKey="customer"
                label="Customer"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            ) : null}
            <SortableTh
              sortKey="used_on_order"
              label="Used on order"
              sortConfig={sortConfig}
              onSort={handleSort}
            />
            <SortableTh
              sortKey="order_value"
              label="Order value"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-end"
            />
            <SortableTh
              sortKey="value"
              label="Credit value"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-end"
            />
            <SortableTh
              sortKey="project_credit_total"
              label="Credit used on project"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-end"
            />
            <SortableTh
              sortKey="balance"
              label="Balance credit"
              sortConfig={sortConfig}
              onSort={handleSort}
              className="text-end"
            />
            <SortableTh
              sortKey="status"
              label="Status"
              sortConfig={sortConfig}
              onSort={handleSort}
            />
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="text-muted text-center py-4">
                No credit notes found.
              </td>
            </tr>
          ) : (
            filtered.map((row) => (
              <tr key={row.id}>
                <td>
                  <button
                    type="button"
                    className="btn btn-link p-0 fw-semibold text-decoration-none border-0 align-baseline"
                    onClick={() => openOriginModal(row)}
                  >
                    {row.identifier}
                  </button>
                </td>
                {showCustomerColumn ? (
                  <td>
                    <CustomerCell usageProjects={row.usage_projects} />
                  </td>
                ) : null}
                <td>
                  <CreditNoteUsageProjects
                    usageProjects={row.usage_projects}
                    showOrderValue={false}
                  />
                </td>
                <td className="text-end">
                  <UsageAmountCell
                    usageProjects={row.usage_projects}
                    field="project_value"
                  />
                </td>
                <td className="text-end">
                  <button
                    type="button"
                    className="btn p-0 border-0 text-end w-100 fw-semibold text-body text-decoration-none bg-transparent shadow-none"
                    onClick={() => openOriginModal(row)}
                    title="View how this credit was calculated"
                  >
                    {fmtOrderRupee(row.value)}
                  </button>
                </td>
                <td className="text-end">
                  <UsageAmountCell
                    usageProjects={row.usage_projects}
                    field="project_credit_total"
                  />
                </td>
                <td className="text-end">
                  <CreditNoteBalance balance={row.balance} />
                </td>
                <td>
                  <CreditNoteStatusBadge status={row.status} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <CreditNoteOriginModal
        isOpen={Boolean(originModalNote?.id)}
        creditNote={originModalNote}
        detailLinkPrefix={detailLinkPrefix}
        onClose={closeOriginModal}
      />
    </>
  );
}
